import {
  del,
  generateClient,
  get,
  isCancelError,
  post,
  put,
} from "aws-amplify/api";
import awsconfig from "../../aws-exports";
import {
  isCancelError as storageIsCancelError,
  uploadData,
} from "aws-amplify/storage";
import { getApiErrorBody } from "../../Utils/StringUtils";

const client = generateClient();

// A 2xx envelope with `success: false` is a failure too. Rethrow it in the
// shape an Amplify HTTP error carries so getErrorMessages reads both the same
// way, instead of letting `res.data` be undefined further down.
async function readResponse(request) {
  const { body } = await request.response;
  const res = await body.json();
  if (res?.success === false) {
    const error = new Error(res.message || "Request failed");
    error.response = { body: res };
    throw error;
  }
  return res;
}

// Runs after a write has already been saved. A failed or cancelled re-read must
// not surface as a failed save (retrying would create a duplicate), so it
// resolves to null and the page's next list fetch shows the record instead.
async function readSavedTransaction(dispatch, requestType, id) {
  const request = client.graphql({
    query: getBusinessAccountingTransaction,
    variables: { id },
  });
  dispatch({ type: requestType, payload: { request } });
  try {
    const res = await request;
    return res.data.getBusinessAccountingTransaction;
  } catch (error) {
    return null;
  }
}

export async function requestCreateAccount(dispatch, payload) {
  try {
    dispatch({ type: "REQUEST_CREATE_ACCOUNT" });
    const { input } = payload;
    const res = await client.graphql({
      query: createAccount,
      variables: { input },
    });
    const account = res.data.createAccount;
    dispatch({ type: "SUCCESS_CREATE_ACCOUNT", payload: { account } });
  } catch (error) {
    dispatch({ type: "ERROR_CREATE_ACCOUNT", error });
  }
}

export async function requestInitAccount(dispatch, payload) {
  try {
    const { businessID } = payload;

    dispatch({ type: "REQUEST_INIT_ACCOUNT", payload: { request: null } });

    const request = post({
      apiName: "accounting",
      path: "/init",
      options: {
        body: {
          businessID,
        },
      },
    });
    dispatch({ type: "REQUEST_INIT_ACCOUNT", payload: { request } });

    await request.response;

    dispatch({
      type: "INIT_ACCOUNT_SUCCESS",
    });
  } catch (error) {
    dispatch({ type: "INIT_ACCOUNT_ERROR", error });
  }
}

export async function requestListAccount(dispatch, payload) {
  try {
    const {
      page,
      businessID,
      limit = 10,
      loadAll = false,
      loadAllLoading = false,
      nextTokens,
    } = payload;

    dispatch({
      type: "REQUEST_LIST_ACCOUNT",
      payload: { request: null, page },
    });

    let items = [];
    let nextToken = nextTokens || "start";

    const params = {
      businessID,
      limit,
      sortDirection: "ASC",
    };

    if (loadAll) {
      while (loadAll && nextToken) {
        if (nextToken && nextToken !== "start") {
          params.nextToken = nextToken;
        }

        const request = client.graphql({
          query: accountsByBusinessCode,
          variables: params,
        });
        dispatch({
          type: "REQUEST_LIST_ALL_ACCOUNT",
          payload: { request, page, loadAllLoading },
        });

        // eslint-disable-next-line no-await-in-loop
        const res = await request;
        items = [...items, ...res.data.accountsByBusinessCode.items];
        nextToken = res.data.accountsByBusinessCode.nextToken;
      }
    } else {
      while (nextToken && items.length === 0) {
        if (nextToken && nextToken !== "start") {
          params.nextToken = nextToken;
        }

        const request = client.graphql({
          query: accountsByBusinessCode,
          variables: params,
        });
        dispatch({
          type: "REQUEST_LIST_ACCOUNT",
          payload: { request, page },
        });

        // eslint-disable-next-line no-await-in-loop
        const res = await request;
        items = [...items, ...res.data.accountsByBusinessCode.items];
        nextToken = res.data.accountsByBusinessCode.nextToken;
      }
    }

    const nextPage = page + 1;

    dispatch({
      type: "LIST_ACCOUNT_SUCCESS",
      payload: {
        items,
        nextToken,
        nextPage,
      },
    });
  } catch (error) {
    dispatch({ type: "LIST_ACCOUNT_ERROR", error });
  }
}

// Whether a business has any account at all. Kept off the shared `accounts`
// slice on purpose: other screens reset it to [] mid-fetch, so a prompt driven
// from it would flash. Throws on request failure so callers can stay silent.
export async function fetchHasAccounts(businessID) {
  let nextToken = null;
  do {
    // eslint-disable-next-line no-await-in-loop
    const res = await client.graphql({
      query: accountsByBusinessCode,
      variables: { businessID, limit: 1, ...(nextToken && { nextToken }) },
    });
    const { items, nextToken: next } = res.data.accountsByBusinessCode;
    if (items.length > 0) return true;
    nextToken = next;
  } while (nextToken);
  return false;
}

export async function requestGetAccountBalance(dispatch, payload) {
  try {
    const { businessID, accountIDs } = payload;

    dispatch({ type: "REQUEST_GET_ACCOUNT_BALANCE" });

    const request = post({
      apiName: "accounting",
      path: "/account-balance",
      options: {
        body: {
          business_id: businessID,
          account_ids: accountIDs,
        },
      },
    });

    const res = await readResponse(request);

    if (!res.data?.accounts) throw new Error("Failed to get account balance");

    const accountBalances = {};
    (res.data?.accounts || []).forEach((account) => {
      accountBalances[account.id] = account;
    });

    dispatch({
      type: "GET_ACCOUNT_BALANCE_SUCCESS",
      payload: { accountBalances },
    });
  } catch (error) {
    dispatch({ type: "GET_ACCOUNT_BALANCE_ERROR", error });
  }
}

export async function requestCreateAccountingAccount(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_CREATE_ACCT_ACCOUNT",
      payload: { request: null },
    });
    const { input } = payload;

    const request = post({
      apiName: "accounting",
      path: "/account",
      options: {
        body: input,
      },
    });
    dispatch({
      type: "REQUEST_CREATE_ACCT_ACCOUNT",
      payload: { request },
    });

    const res = await readResponse(request);

    const account = res.data;

    dispatch({
      type: "CREATE_ACCT_ACCOUNT_SUCCESS",
      payload: {
        account,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      // Keep the action shape identical to the non-cancel branch so the
      // reducer never has to guess whether a payload is present.
      dispatch({
        type: "CREATE_ACCT_ACCOUNT_ERROR",
        error: null,
        payload: { errors: null },
      });
    } else {
      dispatch({
        type: "CREATE_ACCT_ACCOUNT_ERROR",
        error,
        payload: { errors: getApiErrorBody(error) },
      });
    }
  }
}

export async function requestUpdateAccount(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_UPDATE_ACCOUNT",
      payload: { request: null },
    });

    const { input } = payload;

    const request = put({
      apiName: "accounting",
      path: "/account",
      options: {
        body: input,
      },
    });
    dispatch({ type: "REQUEST_UPDATE_ACCOUNT", payload: { request } });

    const res = await readResponse(request);
    const account = res.data;

    dispatch({
      type: "UPDATE_ACCOUNT_SUCCESS",
      payload: { account },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "UPDATE_ACCOUNT_ERROR", error: null });
    } else {
      dispatch({ type: "UPDATE_ACCOUNT_ERROR", error });
    }
  }
}

export async function requestDeleteAccount(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_DELETE_ACCOUNT",
      payload: { request: null },
    });
    const { id } = payload;

    const request = del({
      apiName: "accounting",
      path: `/account/${id}`,
    });
    dispatch({ type: "REQUEST_DELETE_ACCOUNT", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "DELETE_ACCOUNT_SUCCESS",
      payload: { accountID: res.data.id },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "DELETE_ACCOUNT_ERROR", error: null });
    } else {
      dispatch({ type: "DELETE_ACCOUNT_ERROR", error });
    }
  }
}

// AppSync applies `limit` BEFORE the filter, so a page asked for 5 rows can
// come back with one or none while `nextToken` is still set — the Pendapatan
// and Pengeluaran lists showed "no data" at 5 per page and the same month's
// rows at 25. Keep following the token until the page is full, the data runs
// out, or we have asked enough times. `excludeChildren` moves the payment-row
// filter the lists used to apply after rendering into the loop, so it counts
// towards filling the page instead of emptying it again afterwards.
const MAX_LIST_ROUND_TRIPS = 10;

export async function requestListAccountingTransaction(dispatch, payload) {
  const {
    page,
    businessID,
    createdAt = {},
    isJournal = false,
    limit = 10,
    nextToken = null,
    filter = {},
    excludeChildren = false,
    minItems = limit,
  } = payload;
  let transactionType = "ACCOUNTING_TRANSACTION";
  if (isJournal) {
    transactionType = "JOURNAL_TRANSACTION";
  }

  try {
    dispatch({
      type: `REQUEST_LIST_${transactionType}`,
      payload: { request: null, page },
    });

    const collected = [];
    let token = nextToken;
    let rounds = 0;

    do {
      const params = {
        businessID,
        sortDirection: "DESC",
        limit,
        nextToken: token,
      };

      if (Object.keys(filter).length > 0) {
        params.filter = filter;
      }

      if (createdAt) {
        params.createdAt = createdAt;
      }

      const request = client.graphql({
        query: transactionsByBusinessCreatedAt,
        variables: params,
      });
      dispatch({
        type: `REQUEST_LIST_${transactionType}`,
        payload: { request, page },
      });

      const res = await request;
      const { items, nextToken: returnedToken } =
        res.data.transactionsByBusinessCreatedAt;

      collected.push(
        ...(excludeChildren
          ? (items || []).filter((item) => !item.parentTransactionID)
          : items || []),
      );
      token = returnedToken;
      rounds += 1;
    } while (
      token &&
      collected.length < minItems &&
      rounds < MAX_LIST_ROUND_TRIPS
    );

    const nextPage = page + 1;
    dispatch({
      type: `LIST_${transactionType}_SUCCESS`,
      payload: {
        // A page may hold a few more than asked for: the token cannot be split,
        // so trimming the overflow would drop rows nothing would fetch again.
        transactions: collected,
        nextToken: token,
        nextPage,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: `LIST_${transactionType}_CANCEL`,
      });
    } else {
      dispatch({ type: `LIST_${transactionType}_ERROR`, error });
    }
  }
}

export async function requestListCreditAccount(dispatch, payload) {
  try {
    const { businessID, categories = [] } = payload;

    dispatch({
      type: "REQUEST_LIST_CREDIT_ACCOUNT",
      payload: { creditAccountRequest: null },
    });

    let items = [];
    let nextToken = "start";

    const filter = {};
    if (categories.length > 0) {
      filter.or = [];
      categories.forEach((category) => {
        filter.or = [
          ...filter.or,
          {
            category: {
              eq: category,
            },
          },
        ];
      });
    }

    const params = {
      businessID,
      filter,
      sortDirection: "ASC",
    };

    while (nextToken) {
      if (nextToken !== "start") {
        params.nextToken = nextToken;
      }

      const request = client.graphql({
        query: accountsByBusinessCode,
        variables: params,
      });

      dispatch({
        type: "REQUEST_LIST_CREDIT_ACCOUNT",
        payload: { creditAccountRequest: request },
      });

      // eslint-disable-next-line no-await-in-loop
      const res = await request;

      let currentItems = res.data.accountsByBusinessCode.items;
      currentItems = currentItems.sort((a, b) => a.code.localeCompare(b.code));

      items = [...items, ...currentItems];

      nextToken = res.data.accountsByBusinessCode.nextToken;
    }

    dispatch({
      type: "LIST_CREDIT_ACCOUNT_SUCCESS",
      payload: {
        items,
        nextToken,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_CREDIT_ACCOUNT_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_CREDIT_ACCOUNT_ERROR", error });
    }
  }
}

export async function requestListDebitAccount(dispatch, payload) {
  try {
    const { businessID, categories = [] } = payload;

    dispatch({
      type: "REQUEST_LIST_DEBIT_ACCOUNT",
      payload: { debitAccountRequest: null },
    });

    let items = [];
    let nextToken = "start";

    const filter = {};
    if (categories.length > 0) {
      filter.or = [];
      categories.forEach((category) => {
        filter.or = [
          ...filter.or,
          {
            category: {
              eq: category,
            },
          },
        ];
      });
    }

    const params = {
      businessID,
      filter,
      sortDirection: "ASC",
    };

    while (nextToken) {
      if (nextToken !== "start") {
        params.nextToken = nextToken;
      }

      const request = client.graphql({
        query: accountsByBusinessCode,
        variables: params,
      });
      dispatch({
        type: "REQUEST_LIST_DEBIT_ACCOUNT",
        payload: { debitAccountRequest: request },
      });

      // eslint-disable-next-line no-await-in-loop
      const res = await request;

      let currentItems = res.data.accountsByBusinessCode.items;
      currentItems = currentItems.sort((a, b) => a.code.localeCompare(b.code));

      items = [...items, ...currentItems];
      nextToken = res.data.accountsByBusinessCode.nextToken;
    }

    dispatch({
      type: "LIST_DEBIT_ACCOUNT_SUCCESS",
      payload: {
        items,
        nextToken,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_DEBIT_ACCOUNT_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_DEBIT_ACCOUNT_ERROR", error });
    }
  }
}

export async function requestCreateAcctTransaction(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_CREATE_ACCT_TRANSACTION",
      payload: { request: null },
    });
    const { input } = payload;

    const request = post({
      apiName: "accounting",
      path: "/transaction",
      options: {
        body: input,
      },
    });
    dispatch({
      type: "REQUEST_CREATE_ACCT_TRANSACTION",
      payload: { request },
    });

    const res = await readResponse(request);

    const transactionID = res.data.transaction_id;

    const transaction = await readSavedTransaction(
      dispatch,
      "REQUEST_CREATE_ACCT_TRANSACTION",
      transactionID,
    );

    dispatch({
      type: "CREATE_ACCT_TRANSACTION_SUCCESS",
      payload: { transactionID, transaction },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "CREATE_ACCT_TRANSACTION_ERROR", error: null });
    } else {
      // Raw error retains `.response.body` (the Lambda's { success, message,
      // error } envelope) which the page + getErrorMessages() parse to surface
      // the backend message — e.g. the business write-block 403.
      dispatch({ type: "CREATE_ACCT_TRANSACTION_ERROR", error });
    }
  }
}

export async function requestUpdateAcctTransaction(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_UPDATE_ACCT_TRANSACTION",
      payload: { request: null },
    });

    const { input } = payload;

    const request = put({
      apiName: "accounting",
      path: "/transaction",
      options: {
        body: input,
      },
    });
    dispatch({ type: "REQUEST_UPDATE_ACCT_TRANSACTION", payload: { request } });

    const res = await readResponse(request);
    const transactionID = res.data.transaction_id;

    const updatedTransaction = await readSavedTransaction(
      dispatch,
      "REQUEST_UPDATE_ACCT_TRANSACTION",
      transactionID,
    );

    dispatch({
      type: "UPDATE_ACCT_TRANSACTION_SUCCESS",
      payload: { transactionID, transaction: updatedTransaction },
    });
  } catch (error) {
    dispatch({ type: "UPDATE_ACCT_TRANSACTION_ERROR", error });
  }
}

export async function requestDeleteAcctTransaction(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_DELETE_ACCT_TRANSACTION",
      payload: { request: null },
    });
    const { id } = payload;

    const request = del({
      apiName: "accounting",
      path: `/transaction/${id}`,
    });
    dispatch({ type: "REQUEST_DELETE_ACCT_TRANSACTION", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "DELETE_ACCT_TRANSACTION_SUCCESS",
      payload: { transactionID: res.data.transaction_id },
    });
  } catch (error) {
    dispatch({ type: "DELETE_ACCT_TRANSACTION_ERROR", error });
  }
}

export async function requestGetAccountingTransaction(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_GET_ACCOUNTING_TRANSACTION",
      payload: { request: null },
    });
    const { id } = payload;

    const request = client.graphql({
      query: getBusinessAccountingTransaction,
      variables: { id },
    });
    dispatch({
      type: "REQUEST_GET_ACCOUNTING_TRANSACTION",
      payload: { request },
    });

    const res = await request;

    dispatch({
      type: "GET_ACCOUNTING_TRANSACTION_SUCCESS",
      payload: { transaction: res.data.getBusinessAccountingTransaction },
    });
  } catch (error) {
    dispatch({ type: "GET_ACCOUNTING_TRANSACTION_ERROR", error });
  }
}

export async function requestGetParentAccountingTransaction(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_GET_PARENT_ACCT_TRANSACTION",
      payload: { request: null },
    });
    const { id } = payload;

    const request = client.graphql({
      query: getBusinessAccountingTransaction,
      variables: { id },
    });
    dispatch({
      type: "REQUEST_GET_PARENT_ACCT_TRANSACTION",
      payload: { request },
    });

    const res = await request;

    dispatch({
      type: "SUCCESS_GET_PARENT_ACCT_TRANSACTION",
      payload: { parentTransaction: res.data.getBusinessAccountingTransaction },
    });
  } catch (error) {
    dispatch({ type: "ERROR_GET_PARENT_ACCT_TRANSACTION", error });
  }
}

export async function requestListAccountBalance(dispatch, payload) {
  try {
    const { businessID, startDate, endDate } = payload;

    dispatch({
      type: "REQUEST_LIST_ACCOUNT_BALANCE",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: "/trial-balance",
      options: {
        body: {
          business_id: businessID,
          start_date: startDate,
          end_date: endDate,
        },
      },
    });
    dispatch({ type: "REQUEST_LIST_ACCOUNT_BALANCE", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "LIST_ACCOUNT_BALANCE_SUCCESS",
      payload: {
        // A deleted account leaves its balance row behind with a null
        // relation; sorting through it used to break the list before any page
        // could render it.
        items: res.data.items.sort((a, b) =>
          (a.account?.code || "").localeCompare(b.account?.code || ""),
        ),
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_ACCOUNT_BALANCE_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_ACCOUNT_BALANCE_ERROR", error });
    }
  }
}

export async function requestExportAccountBalance(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, exportType } = payload;

    dispatch({
      type: "REQUEST_EXPORT_ACCOUNT_BALANCE",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: `/trial-balance?export=${exportType}`,
      options: {
        body: {
          business_id: businessID,
          start_date: startDate,
          end_date: endDate,
        },
      },
    });
    dispatch({ type: "REQUEST_EXPORT_ACCOUNT_BALANCE", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "EXPORT_ACCOUNT_BALANCE_SUCCESS",
      payload: {
        file: res.data,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({ type: "EXPORT_ACCOUNT_BALANCE_ERROR", error: null });
    } else if (isCancelError(error)) {
      dispatch({ type: "EXPORT_ACCOUNT_BALANCE_ERROR", error: null });
    } else {
      dispatch({ type: "EXPORT_ACCOUNT_BALANCE_ERROR", error });
    }
  }
}

export async function requestListIncomeStatement(dispatch, payload) {
  try {
    const { businessID, startDate, endDate } = payload;

    dispatch({
      type: "REQUEST_LIST_INCOME_STATEMENT",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: "/income-statement",
      options: {
        body: {
          business_id: businessID,
          start_date: startDate,
          end_date: endDate,
        },
      },
    });
    dispatch({ type: "REQUEST_LIST_INCOME_STATEMENT", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "LIST_INCOME_STATEMENT_SUCCESS",
      payload: {
        items: res.data.items,
        // Totals computed by the API, the same figures the mobile app and
        // the PDF export use (profitBeforeTax, taxExpense, netIncome, ...).
        summary: {
          otherExpenseNonTax: res.data.otherExpenseNonTax,
          profitBeforeTax: res.data.profitBeforeTax,
          taxExpense: res.data.taxExpense,
          netIncome: res.data.netIncome,
        },
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_INCOME_STATEMENT_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_INCOME_STATEMENT_ERROR", error });
    }
  }
}

export async function requestExportIncomeStatement(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, exportType } = payload;

    dispatch({
      type: "REQUEST_EXPORT_INCOME_STATEMENT",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: `/income-statement?export=${exportType}`,
      options: {
        body: {
          business_id: businessID,
          start_date: startDate,
          end_date: endDate,
        },
      },
    });
    dispatch({ type: "REQUEST_EXPORT_INCOME_STATEMENT", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "EXPORT_INCOME_STATEMENT_SUCCESS",
      payload: {
        file: res.data,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({ type: "EXPORT_INCOME_STATEMENT_ERROR", error: null });
    } else if (isCancelError(error)) {
      dispatch({ type: "EXPORT_INCOME_STATEMENT_ERROR", error: null });
    } else {
      dispatch({ type: "EXPORT_INCOME_STATEMENT_ERROR", error });
    }
  }
}

// `period` is one tax month, "YYYY-MM".
export async function requestVatRecap(dispatch, payload) {
  try {
    const { businessID, period } = payload;

    dispatch({
      type: "REQUEST_VAT_RECAP",
      payload: { request: null, period },
    });

    const request = post({
      apiName: "accounting",
      path: "/vat-recap",
      options: {
        body: {
          business_id: businessID,
          period,
        },
      },
    });
    dispatch({ type: "REQUEST_VAT_RECAP", payload: { request, period } });

    const res = await readResponse(request);
    // The page shows its skeleton until a recap or an error arrives.
    if (!res.data) throw new Error("Failed to get VAT recap");

    dispatch({ type: "VAT_RECAP_SUCCESS", payload: { recap: res.data, period } });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "VAT_RECAP_CANCEL" });
    } else {
      dispatch({
        type: "VAT_RECAP_ERROR",
        error,
        payload: { period: payload.period },
      });
    }
  }
}

export async function requestExportVatRecap(dispatch, payload) {
  try {
    const { businessID, period, exportType } = payload;

    dispatch({ type: "REQUEST_EXPORT_VAT_RECAP", payload: { request: null } });

    const request = post({
      apiName: "accounting",
      path: `/vat-recap?export=${exportType}`,
      options: {
        body: {
          business_id: businessID,
          period,
        },
      },
    });
    dispatch({ type: "REQUEST_EXPORT_VAT_RECAP", payload: { request } });

    const res = await readResponse(request);

    dispatch({ type: "EXPORT_VAT_RECAP_SUCCESS", payload: { file: res.data } });
  } catch (error) {
    if (storageIsCancelError(error) || isCancelError(error)) {
      dispatch({ type: "EXPORT_VAT_RECAP_ERROR", error: null });
    } else {
      dispatch({ type: "EXPORT_VAT_RECAP_ERROR", error });
    }
  }
}

// One transaction read straight from the API, leaving reducer state alone: for
// pages that open a record named in their URL, where writing it into the shared
// `transaction` slot would race whatever the page itself loads there.
export async function fetchAccountingTransaction(id) {
  const res = await client.graphql({
    query: getBusinessAccountingTransaction,
    variables: { id },
  });
  return res.data.getBusinessAccountingTransaction;
}

// Re-reads one transaction after a write made outside /transaction (a deposit
// applied to an invoice, say) and swaps it into the list and the detail slot
// without touching their loading flags, so an open drawer does not flash.
export async function refreshAccountingTransaction(dispatch, id) {
  const transaction = await fetchAccountingTransaction(id);
  if (transaction) {
    dispatch({
      type: "REFRESH_ACCT_TRANSACTION_SUCCESS",
      payload: { transaction },
    });
  }
  return transaction;
}

// A customer's sales invoices that still have a balance, newest first, for
// "Pakai di Invoice" on a deposit. Read straight from the API so the page's
// own transaction list is left alone.
//
// There is no index on partner + paid, so the type/paid filter runs after
// `limit` and every round scans one page of the partner's transactions. The
// pages are large to reach far back, but the rounds are still capped:
// `truncated` says the oldest transactions were never looked at, so the
// picker can say an older invoice may be missing instead of "none".
const UNPAID_INVOICES_PAGE = 1000;

export async function fetchUnpaidPartnerInvoices(partnerID) {
  const invoices = [];
  let nextToken = null;
  let rounds = 0;
  do {
    // eslint-disable-next-line no-await-in-loop
    const res = await client.graphql({
      query: transactionsByPartnerCreatedAt,
      variables: {
        partnerID,
        sortDirection: "DESC",
        limit: UNPAID_INVOICES_PAGE,
        nextToken,
        filter: {
          type: { eq: "INCOME_RECEIVABLE" },
          paid: { ne: true },
        },
      },
    });
    const { items, nextToken: token } = res.data.transactionsByPartnerCreatedAt;
    invoices.push(...(items || []).filter((item) => !item.parentTransactionID));
    nextToken = token;
    rounds += 1;
  } while (nextToken && rounds < MAX_LIST_ROUND_TRIPS);
  return { items: invoices, truncated: Boolean(nextToken) };
}

export async function requestCashFlow(dispatch, payload) {
  try {
    const {
      businessID,
      startDate,
      endDate,
      withTransactions = false,
      page = 1,
      limit = 5,
    } = payload;

    dispatch({ type: "REQUEST_CASH_FLOW", payload: { request: null } });

    let inputBody = {
      business_id: businessID,
      start_date: startDate,
      end_date: endDate,
    };

    if (withTransactions) {
      inputBody = {
        ...inputBody,
        with_transactions: withTransactions,
        page,
        limit,
      };
    }

    const request = post({
      apiName: "accounting",
      path: "/cash-flow",
      options: {
        body: inputBody,
      },
    });
    dispatch({ type: "REQUEST_CASH_FLOW", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "CASH_FLOW_SUCCESS",
      payload: {
        items: res.data.items,
        cashFlow: res.data.cashFlow,
        previousDate: res.data.previousDate,
        previousCash: res.data.previousCash,
        endDate: res.data.endDate,
        endCash: res.data.endCash,
        transactions: res.data.transactions || [],
        transactionsTotal: res.data.transactionsTotal || 0,
        transactionsTotalPages: res.data.transactionsTotalPages || 1,
        // Additive PSAK 2 reconciliation flags (default to reconciled when the
        // backend predates them so no false warning is shown).
        reconciled: res.data.reconciled !== false,
        difference: res.data.difference || 0,
        // PSAK 2 (43) non-cash investing/financing disclosure. Empty array when
        // the backend predates it, so nothing renders.
        nonCashActivities: res.data.nonCashActivities || [],
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "CASH_FLOW_CANCEL",
      });
    } else {
      dispatch({ type: "CASH_FLOW_ERROR", error });
    }
  }
}

export async function requestListBalanceSheet(dispatch, payload) {
  try {
    const { businessID, date } = payload;

    dispatch({
      type: "REQUEST_LIST_BALANCE_SHEET",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: "/balance-sheet",
      options: {
        body: {
          business_id: businessID,
          date,
        },
      },
    });
    dispatch({ type: "REQUEST_LIST_BALANCE_SHEET", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "LIST_BALANCE_SHEET_SUCCESS",
      payload: {
        items: res.data,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "LIST_BALANCE_SHEET_CANCEL",
      });
    } else {
      dispatch({ type: "LIST_BALANCE_SHEET_ERROR", error });
    }
  }
}

export async function requestExportBalanceSheet(dispatch, payload) {
  try {
    const { businessID, date, exportType } = payload;

    dispatch({
      type: "REQUEST_EXPORT_BALANCE_SHEET",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: `/balance-sheet?export=${exportType}`,
      options: {
        body: {
          business_id: businessID,
          date,
        },
      },
    });
    dispatch({ type: "REQUEST_EXPORT_BALANCE_SHEET", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "EXPORT_BALANCE_SHEET_SUCCESS",
      payload: {
        file: res.data,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({ type: "EXPORT_BALANCE_SHEET_ERROR", error: null });
    } else if (isCancelError(error)) {
      dispatch({ type: "EXPORT_BALANCE_SHEET_ERROR", error: null });
    } else {
      dispatch({ type: "EXPORT_BALANCE_SHEET_ERROR", error });
    }
  }
}

export async function requestExportCashflow(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, exportType } = payload;

    dispatch({
      type: "REQUEST_EXPORT_CASH_FLOW",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: `/cash-flow?export=${exportType}`,
      options: {
        body: {
          business_id: businessID,
          start_date: startDate,
          end_date: endDate,
        },
      },
    });
    dispatch({ type: "REQUEST_EXPORT_CASH_FLOW", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "EXPORT_CASH_FLOW_SUCCESS",
      payload: {
        file: res.data,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({ type: "EXPORT_CASH_FLOW_ERROR", error: null });
    } else if (isCancelError(error)) {
      dispatch({ type: "EXPORT_CASH_FLOW_ERROR", error: null });
    } else {
      dispatch({ type: "EXPORT_CASH_FLOW_ERROR", error });
    }
  }
}

export async function requestCapitalStatement(dispatch, payload) {
  try {
    const { businessID, date } = payload;

    dispatch({
      type: "REQUEST_CAPITAL_STATEMENT",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: "/capital-statement",
      options: {
        body: { business_id: businessID, date },
      },
    });
    dispatch({ type: "REQUEST_CAPITAL_STATEMENT", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "CAPITAL_STATEMENT_SUCCESS",
      payload: {
        items: res.data.items,
        capital: res.data.capital,
        netIncome: res.data.netIncome,
        beginningCapital: res.data.beginningCapital,
        retainedEarningsPrior: res.data.retainedEarningsPrior,
        netIncomeCurrent: res.data.netIncomeCurrent,
        additions: res.data.additions,
        withdrawals: res.data.withdrawals,
        matrix: res.data.matrix,
      },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "CAPITAL_STATEMENT_CANCEL",
      });
    } else {
      dispatch({ type: "CAPITAL_STATEMENT_ERROR", error });
    }
  }
}

export async function requestExportCapitalStatement(dispatch, payload) {
  try {
    const { businessID, date, exportType } = payload;

    dispatch({
      type: "REQUEST_EXPORT_CAPITAL_STATEMENT",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: `/capital-statement?export=${exportType}`,
      options: {
        body: {
          business_id: businessID,
          date: date,
        },
      },
    });
    dispatch({
      type: "REQUEST_EXPORT_CAPITAL_STATEMENT",
      payload: { request },
    });

    const res = await readResponse(request);

    dispatch({
      type: "EXPORT_CAPITAL_STATEMENT_SUCCESS",
      payload: {
        file: res.data,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({ type: "EXPORT_CAPITAL_STATEMENT_ERROR", error: null });
    } else if (isCancelError(error)) {
      dispatch({ type: "EXPORT_CAPITAL_STATEMENT_ERROR", error: null });
    } else {
      dispatch({ type: "EXPORT_CAPITAL_STATEMENT_ERROR", error });
    }
  }
}

export async function requestDocumentExcelAccUpload(dispatch, payload) {
  const { file, businessID } = payload;

  try {
    dispatch({ type: "REQUEST_UPLOAD_DOCUMENT_EXCEL" });

    const key = `accounting/${file.name}`;

    const upload = uploadData({
      path: ({ identityId }) => `private/${identityId}/${key}`,
      data: file,
      options: {
        contentType: file.type,
        onProgress: (progress) => {
          dispatch({
            type: "UPLOAD_DOCUMENT_EXCEL_PENDING",
            payload: { progress, upload },
          });
        },
      },
    });

    const result = await upload.result;

    const input = {
      business_id: businessID,
    };

    if (result.path) {
      input.file = {
        key,
        bucket: awsconfig.aws_user_files_s3_bucket,
        region: awsconfig.aws_user_files_s3_bucket_region,
      };
    }

    const request = post({
      apiName: "accounting",
      path: "/upload/account",
      options: {
        body: input,
      },
    });

    const res = await readResponse(request);
    const excelUpload = res.data;

    dispatch({
      type: "UPLOAD_DOCUMENT_EXCEL_SUCCESS",
      payload: {
        excelUpload,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({
        type: "UPLOAD_DOCUMENT_EXCEL_ERROR",
        error: null,
      });
    } else {
      dispatch({ type: "UPLOAD_DOCUMENT_EXCEL_ERROR", error });
    }
  }
}

export async function requestCreateDocumentExcelAccount(dispatch, payload) {
  const { accounts, businessID } = payload;
  try {
    dispatch({ type: "REQUEST_CREATE_EXCEL_ACCOUNT" });

    const request = post({
      apiName: "accounting",
      path: "/bulk/account",
      options: {
        body: {
          business_id: businessID,
          accounts,
        },
      },
    });

    const res = await readResponse(request);
    const { items } = res.data;

    dispatch({
      type: "SUCCESS_CREATE_EXCEL_ACCOUNT",
      payload: { excelAccounts: items },
    });
  } catch (error) {
    dispatch({ type: "ERROR_CREATE_EXCEL_ACCOUNT", error });
  }
}

export async function requestListPartnerTransaction(dispatch, payload) {
  try {
    const {
      page,
      partnerID,
      limit = 10,
      createdAt,
      sortDirection = "DESC",
      nextToken = null,
    } = payload;

    dispatch({ type: "REQUEST_LIST_PARTNER_TRANSACTION", payload: { page } });
    const params = {
      partnerID,
      limit,
      sortDirection,
      nextToken,
    };
    if (createdAt) {
      params.createdAt = createdAt;
    }
    const res = await client.graphql({
      query: transactionsByPartnerCreatedAt,
      variables: params,
    });
    const { items, nextToken: token } = res.data.transactionsByPartnerCreatedAt;
    const nextPage = page + 1;
    dispatch({
      type: "SUCCESS_LIST_PARTNER_TRANSACTION",
      payload: { items, nextToken: token, nextPage },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_PARTNER_TRANSACTION", payload: { error } });
  }
}

export async function requestCreateInvoiceTransaction(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_CREATE_INVOICE_TRANSACTION",
      payload: { request: null },
    });
    const { input } = payload;

    const request = post({
      apiName: "accounting",
      path: "/invoice",
      options: {
        body: input,
      },
    });
    dispatch({
      type: "REQUEST_CREATE_INVOICE_TRANSACTION",
      payload: { request },
    });

    const res = await readResponse(request);
    const transactionID = res.data.transaction_id;

    const invoice = await readSavedTransaction(
      dispatch,
      "REQUEST_CREATE_INVOICE_TRANSACTION",
      transactionID,
    );

    dispatch({
      type: "CREATE_INVOICE_TRANSACTION_SUCCESS",
      payload: { transactionID, invoice },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "CREATE_INVOICE_TRANSACTION_CANCEL",
      });
    } else {
      dispatch({ type: "CREATE_INVOICE_TRANSACTION_ERROR", error });
    }
  }
}

export async function requestUpdateInvoiceTransaction(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_UPDATE_INVOICE_TRANSACTION",
      payload: { request: null },
    });
    const { input } = payload;

    const request = put({
      apiName: "accounting",
      path: "/invoice",
      options: {
        body: input,
      },
    });
    dispatch({
      type: "REQUEST_UPDATE_INVOICE_TRANSACTION",
      payload: { request },
    });

    const res = await readResponse(request);
    const invoiceID = res.data.transaction_id;

    const invoice = await readSavedTransaction(
      dispatch,
      "REQUEST_UPDATE_INVOICE_TRANSACTION",
      invoiceID,
    );

    dispatch({
      type: "UPDATE_INVOICE_TRANSACTION_SUCCESS",
      payload: { invoiceID, invoice },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({
        type: "UPDATE_INVOICE_TRANSACTION_CANCEL",
      });
    } else {
      dispatch({ type: "UPDATE_INVOICE_TRANSACTION_ERROR", error });
    }
  }
}

export async function requestDownloadInvoice(dispatch, payload) {
  try {
    const { transactionId } = payload;

    dispatch({
      type: "REQUEST_DOWNLOAD_INVOICE",
      payload: { request: null },
    });

    const request = get({
      apiName: "accounting",
      path: `/invoice?export=pdf&transaction_id=${transactionId}`,
    });

    dispatch({ type: "REQUEST_DOWNLOAD_INVOICE", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "DOWNLOAD_INVOICE_SUCCESS",
      payload: {
        file: res.data,
      },
    });
  } catch (error) {
    dispatch({ type: "DOWNLOAD_INVOICE_ERROR", error });
  }
}

export async function requestUpdateOpeningBalance(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_UPDATE_OPENING_BALANCE",
      payload: { request: null },
    });
    const { input } = payload;

    const request = post({
      apiName: "accounting",
      path: "/opening-balance",
      options: {
        body: input,
      },
    });
    dispatch({
      type: "REQUEST_UPDATE_OPENING_BALANCE",
      payload: { request },
    });

    const res = await readResponse(request);

    const transactionID = res.data.transaction_id;

    dispatch({
      type: "UPDATE_OPENING_BALANCE_SUCCESS",
      payload: {
        transactionID,
      },
    });
  } catch (error) {
    dispatch({ type: "UPDATE_OPENING_BALANCE_ERROR", error });
  }
}

export async function requestAccountingSummary(dispatch, payload) {
  try {
    const { businessID, startDate, endDate } = payload;

    dispatch({
      type: "REQUEST_ACOUNTING_SUMMARY",
      payload: { request: null },
    });

    const request = get({
      apiName: "accounting",
      path: `/summary?business_id=${businessID}&start_date=${startDate}&end_date=${endDate}`,
    });
    dispatch({ type: "REQUEST_ACOUNTING_SUMMARY", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "ACOUNTING_SUMMARY_SUCCESS",
      payload: {
        accountingSummary: res.data,
      },
    });
  } catch (error) {
    dispatch({ type: "ACOUNTING_SUMMARY_ERROR", error });
  }
}

export async function requestExportGeneralJournal(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, exportType } = payload;

    dispatch({
      type: "REQUEST_EXPORT_GENERAL_JOURNAL",
      payload: { request: null },
    });

    const request = post({
      apiName: "accounting",
      path: `/general-journal?export=${exportType}`,
      options: {
        body: {
          business_id: businessID,
          start_date: startDate,
          end_date: endDate,
        },
      },
    });
    dispatch({ type: "REQUEST_EXPORT_GENERAL_JOURNAL", payload: { request } });

    const res = await readResponse(request);

    dispatch({
      type: "EXPORT_GENERAL_JOURNAL_SUCCESS",
      payload: {
        file: res.data,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({ type: "EXPORT_GENERAL_JOURNAL_ERROR", error: null });
    } else if (isCancelError(error)) {
      dispatch({ type: "EXPORT_GENERAL_JOURNAL_ERROR", error: null });
    } else {
      dispatch({ type: "EXPORT_GENERAL_JOURNAL_ERROR", error });
    }
  }
}

// Debit/credit totals for the whole period, not just the page the list shows.
// Same body as the export, so the cards match the exported file.
export async function requestGeneralJournalSummary(dispatch, payload) {
  let request;
  try {
    const { businessID, startDate, endDate } = payload;

    request = post({
      apiName: "accounting",
      path: "/general-journal?summary=true",
      options: {
        body: {
          business_id: businessID,
          start_date: startDate,
          end_date: endDate,
        },
      },
    });
    dispatch({
      type: "REQUEST_GENERAL_JOURNAL_SUMMARY",
      payload: { request },
    });

    const res = await readResponse(request);

    dispatch({
      type: "GENERAL_JOURNAL_SUMMARY_SUCCESS",
      payload: { summary: res.data, request },
    });
  } catch (error) {
    // A cancelled request was superseded by a newer one that already owns the
    // loading state; reporting it would end that state early.
    if (isCancelError(error)) return;
    dispatch({
      type: "GENERAL_JOURNAL_SUMMARY_ERROR",
      error,
      payload: { request },
    });
  }
}

export async function requestDocumentExcelAccTransactionUpload(
  dispatch,
  payload,
) {
  const { file, businessID, useDetection } = payload;

  try {
    dispatch({ type: "REQUEST_UPLOAD_DOCUMENT_EXCEL" });

    const key = `accounting/${file.name}`;

    const upload = uploadData({
      path: ({ identityId }) => `private/${identityId}/${key}`,
      data: file,
      options: {
        contentType: file.type,
        onProgress: (progress) => {
          dispatch({
            type: "UPLOAD_DOCUMENT_EXCEL_PENDING",
            payload: { progress, upload },
          });
        },
      },
    });

    const result = await upload.result;

    const input = {
      use_detection: useDetection || false,
      business_id: businessID,
    };

    if (result.path) {
      input.file = {
        key,
        bucket: awsconfig.aws_user_files_s3_bucket,
        region: awsconfig.aws_user_files_s3_bucket_region,
      };
    }

    const request = post({
      apiName: "accounting",
      path: "/upload/transaction",
      options: {
        body: input,
      },
    });

    const res = await readResponse(request);
    const excelUpload = res.data;
    const excelUploadError = res.error;

    dispatch({
      type: "UPLOAD_DOCUMENT_EXCEL_SUCCESS",
      payload: {
        excelUpload,
        excelUploadError,
        // This endpoint answers 200 even when it rejects rows, so nothing here
        // ever reaches the error path. The period fields sit at the top of the
        // body — outside `excelUploadError` — and would be dropped entirely if
        // they were not carried across explicitly.
        excelUploadClosedRows: res.closedRows,
        excelUploadClosedThrough: res.closedThrough,
      },
    });
  } catch (error) {
    if (storageIsCancelError(error)) {
      dispatch({
        type: "UPLOAD_DOCUMENT_EXCEL_ERROR",
        error: null,
      });
    } else {
      dispatch({ type: "UPLOAD_DOCUMENT_EXCEL_ERROR", error });
    }
  }
}

export async function requestCreateDocumentExcelTransaction(dispatch, payload) {
  const { transactions, businessID } = payload;
  try {
    dispatch({ type: "REQUEST_CREATE_EXCEL_TRANSACTION" });

    const request = post({
      apiName: "accounting",
      path: "/bulk/transaction",
      options: {
        body: {
          business_id: businessID,
          transactions,
        },
      },
    });

    const res = await readResponse(request);
    const items = res.data.transactions;

    dispatch({
      type: "SUCCESS_CREATE_EXCEL_TRANSACTION",
      payload: { excelTransactions: items },
    });
  } catch (error) {
    dispatch({ type: "ERROR_CREATE_EXCEL_TRANSACTION", error });
  }
}

export async function requestGetAccountingTransactionImport(dispatch, payload) {
  try {
    dispatch({
      type: "REQUEST_GET_ACCOUNTING_TRANSACTION_IMPORT",
      payload: { request: null },
    });
    const { id } = payload;

    const request = client.graphql({
      query: getAccountingTransactionImport,
      variables: { id },
    });
    dispatch({
      type: "REQUEST_GET_ACCOUNTING_TRANSACTION_IMPORT",
      payload: { request },
    });

    const res = await request;
    const draftImport = res.data.getAccountingTransactionImport;

    if (!draftImport) throw new Error("draftImportNotfound");

    dispatch({
      type: "GET_ACCOUNTING_TRANSACTION_IMPORT_SUCCESS",
      payload: { draftImport },
    });
  } catch (error) {
    dispatch({ type: "GET_ACCOUNTING_TRANSACTION_IMPORT_ERROR", error });
  }
}

export async function requestListAccountingTransactionImport(
  dispatch,
  payload,
) {
  const {
    businessID,
    page,
    limit = 10,
    nextToken = "start",
    sortDirection = "DESC",
  } = payload;
  try {
    dispatch({
      type: "REQUEST_LIST_ACCOUNTING_TRANSACTION_IMPORT",
      payload: { request: null, page },
    });

    const params = { limit, sortDirection, businessID };

    let items = [];
    let token = "start";
    if (nextToken) {
      token = nextToken;
    }

    while (token && items.length === 0) {
      if (token && token !== "start") {
        params.nextToken = token;
      }

      const request = client.graphql({
        query: importsByBusinessCreatedAt,
        variables: params,
      });

      dispatch({
        type: "REQUEST_LIST_ACCOUNTING_TRANSACTION_IMPORT",
        payload: { request },
      });

      const res = await request;
      items = [...items, ...res.data.importsByBusinessCreatedAt.items];
      token = res.data.importsByBusinessCreatedAt.nextToken;
    }

    dispatch({
      type: "SUCCESS_LIST_ACCOUNTING_TRANSACTION_IMPORT",
      payload: { items, nextToken: token, nextPage: page + 1 },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_ACCOUNTING_TRANSACTION_IMPORT", error });
  }
}

export async function requestGetIncomeStats(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, prevStartDate, prevEndDate } =
      payload;

    dispatch({
      type: "REQUEST_GET_INCOME_STATS",
      payload: { request: null },
    });

    const params = {
      business_id: businessID,
      start_date: startDate,
      end_date: endDate,
      prev_start_date: prevStartDate,
      prev_end_date: prevEndDate,
    };

    const request = post({
      apiName: "accounting",
      path: "/stats/income",
      options: {
        body: params,
      },
    });

    dispatch({
      type: "REQUEST_GET_INCOME_STATS",
      payload: { request: request },
    });

    const res = await readResponse(request);

    dispatch({
      type: "GET_INCOME_STATS_SUCCESS",
      payload: { incomeStats: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "GET_INCOME_STATS_CANCEL" });
    } else {
      dispatch({ type: "GET_INCOME_STATS_ERROR", payload: { error } });
    }
  }
}

export async function requestGetExpenseStats(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, prevStartDate, prevEndDate } =
      payload;

    dispatch({
      type: "REQUEST_GET_EXPENSE_STATS",
      payload: { request: null },
    });

    const params = {
      business_id: businessID,
      start_date: startDate,
      end_date: endDate,
      prev_start_date: prevStartDate,
      prev_end_date: prevEndDate,
    };

    const request = post({
      apiName: "accounting",
      path: "/stats/expenses",
      options: {
        body: params,
      },
    });

    dispatch({
      type: "REQUEST_GET_EXPENSE_STATS",
      payload: { request: request },
    });

    const res = await readResponse(request);

    dispatch({
      type: "GET_EXPENSE_STATS_SUCCESS",
      payload: { expenseStats: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "GET_EXPENSE_STATS_CANCEL" });
    } else {
      dispatch({ type: "GET_EXPENSE_STATS_ERROR", payload: { error } });
    }
  }
}

export async function requestGetAccountingTransactionStats(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, prevStartDate, prevEndDate } =
      payload;

    dispatch({
      type: "REQUEST_GET_ACCOUNTING_STATS",
      payload: { request: null },
    });

    const params = {
      business_id: businessID,
      start_date: startDate,
      end_date: endDate,
      prev_start_date: prevStartDate,
      prev_end_date: prevEndDate,
    };

    const request = post({
      apiName: "accounting",
      path: "/stats/transaction",
      options: {
        body: params,
      },
    });

    dispatch({
      type: "REQUEST_GET_ACCOUNTING_STATS",
      payload: { request: request },
    });

    const res = await readResponse(request);

    dispatch({
      type: "GET_ACCOUNTING_STATS_SUCCESS",
      payload: { stats: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "GET_ACCOUNTING_STATS_CANCEL" });
    } else {
      dispatch({ type: "GET_ACCOUNTING_STATS_ERROR", payload: { error } });
    }
  }
}

export async function requestGetAccountingCashflowStats(dispatch, payload) {
  try {
    const { businessID, startDate, endDate, prevStartDate, prevEndDate } =
      payload;

    dispatch({
      type: "REQUEST_GET_CASHFLOW",
      payload: { request: null },
    });

    const params = {
      business_id: businessID,
      start_date: startDate,
      end_date: endDate,
      prev_start_date: prevStartDate,
      prev_end_date: prevEndDate,
    };

    const request = post({
      apiName: "accounting",
      path: "/stats/cashflow",
      options: {
        body: params,
      },
    });

    dispatch({
      type: "REQUEST_GET_CASHFLOW",
      payload: { request: request },
    });

    const res = await readResponse(request);

    dispatch({
      type: "GET_CASHFLOW_SUCCESS",
      payload: { cashFlows: res.data },
    });
  } catch (error) {
    if (isCancelError(error)) {
      dispatch({ type: "GET_CASHFLOW_CANCEL" });
    } else {
      dispatch({ type: "GET_CASHFLOW_ERROR", payload: { error } });
    }
  }
}
export async function requestListAccountTransaction(dispatch, payload) {
  const {
    accountID,
    createdAt,
    page,
    limit = 10,
    nextToken = "start",
    sortDirection = "DESC",
    filter = {},
  } = payload;
  try {
    dispatch({
      type: "REQUEST_LIST_ACCOUNT_TRANSACTION",
      payload: { request: null, page },
    });

    const params = { limit, sortDirection, accountID, createdAt };

    if (Object.keys(filter).length > 0) {
      params.filter = filter;
    }

    let items = [];
    let token = "start";
    if (nextToken) {
      token = nextToken;
    }

    while (token && items.length === 0) {
      if (token && token !== "start") {
        params.nextToken = token;
      }

      const request = client.graphql({
        query: accountTransactionsByAccountCreatedAt,
        variables: params,
      });

      dispatch({
        type: "REQUEST_LIST_ACCOUNT_TRANSACTION",
        payload: { request },
      });

      const res = await request;
      items = [
        ...items,
        ...res.data.accountTransactionsByAccountCreatedAt.items,
      ];
      token = res.data.accountTransactionsByAccountCreatedAt.nextToken;
    }

    dispatch({
      type: "SUCCESS_LIST_ACCOUNT_TRANSACTION",
      payload: { items, nextToken: token, nextPage: page + 1 },
    });
  } catch (error) {
    dispatch({ type: "ERROR_LIST_ACCOUNT_TRANSACTION", error });
  }
}

export function setAccTransactionInitialData(dispatch, payload) {
  dispatch({ type: "SET_ACCT_TRANSACTION_INITIAL_DATA", payload });
}

export function deleteDraftAccTransactionImport(dispatch, payload) {
  dispatch({ type: "DELETE_DRAFT_ACCT_TRANSACTION_IMPORT", payload });
}

export function editDraftAccTransactionImport(dispatch, payload) {
  dispatch({ type: "EDIT_DRAFT_ACCT_TRANSACTION_IMPORT", payload });
}

export async function clearCreateDocumentExcelTransaction(dispatch) {
  dispatch({ type: "CLEAR_CREATE_EXCEL_TRANSACTION" });
}

export async function clearExportGeneralJournal(dispatch) {
  dispatch({ type: "CLEAR_EXPORT_GENERAL_JOURNAL" });
}

export async function clearUpdateOpeningBalance(dispatch) {
  dispatch({ type: "CLEAR_UPDATE_OPENING_BALANCE" });
}

export async function clearDownloadInvoice(dispatch) {
  dispatch({ type: "CLEAR_DOWNLOAD_INVOICE" });
}

export async function clearCreateDocumentExcelAccount(dispatch) {
  dispatch({ type: "CLEAR_CREATE_EXCEL_ACCOUNT" });
}

export async function clearDocumentExcelAccUpload(dispatch) {
  dispatch({ type: "CLEAR_UPLOAD_DOCUMENT_EXCEL" });
}

export async function clearDocumentExcelUploadStatus(dispatch) {
  dispatch({ type: "CLEAR_UPLOAD_STATUS_DOCUMENT_EXCEL" });
}

export async function clearDocumentExcelAccountError(dispatch) {
  dispatch({ type: "CLEAR_EXCEL_ACCOUNT_ERROR" });
}

export async function clearAccountingTransaction(dispatch) {
  dispatch({ type: "CLEAR_ACCOUNTING_TRANSACTION" });
}

export async function clearAcctTransactionError(dispatch) {
  dispatch({ type: "CLEAR_ACCT_TRANSACTION_ERROR" });
}

export async function clearInitAccountStatus(dispatch) {
  dispatch({ type: "CLEAR_INIT_ACCOUNT_STATUS" });
}

export async function clearListAccount(dispatch) {
  dispatch({ type: "CLEAR_LIST_ACCOUNT" });
}

export async function clearAccountBalance(dispatch) {
  dispatch({ type: "CLEAR_ACCOUNT_BALANCE" });
}

export async function clearExportBalance(dispatch) {
  dispatch({ type: "CLEAR_EXPORT_BALANCE" });
}

export async function clearIncomeStatement(dispatch) {
  dispatch({ type: "CLEAR_INCOME_STATEMENT" });
}

export async function clearExportIncomeStatement(dispatch) {
  dispatch({ type: "CLEAR_EXPORT_INCOME_STATEMENT" });
}

export async function clearVatRecap(dispatch) {
  dispatch({ type: "CLEAR_VAT_RECAP" });
}

export async function clearExportVatRecap(dispatch) {
  dispatch({ type: "CLEAR_EXPORT_VAT_RECAP" });
}

export async function clearExportCapitalStatement(dispatch) {
  dispatch({ type: "CLEAR_EXPORT_CAPITAL_STATEMENT" });
}

export async function clearExportCashflow(dispatch) {
  dispatch({ type: "CLEAR_EXPORT_CASH_FLOW" });
}

export async function clearAccountingAccount(dispatch) {
  dispatch({ type: "CLEAR_ACCOUNTING_ACCOUNT" });
}

export async function clearAcctAccountError(dispatch) {
  dispatch({ type: "CLEAR_ACCT_ACCOUNT_ERROR" });
}

export async function clearExportBalanceSheet(dispatch) {
  dispatch({ type: "CLEAR_EXPORT_BALANCE_SHEET" });
}

export async function clearInvoiceTransaction(dispatch) {
  dispatch({ type: "CLEAR_INVOICE_TRANSACTION" });
}

export async function clearInvoiceTransactionError(dispatch) {
  dispatch({ type: "CLEAR_INVOICE_TRANSACTION_ERROR" });
}

export async function clearAccountTransaction(dispatch) {
  dispatch({ type: "CLEAR_ACCOUNT_TRANSACTION" });
}

export async function clearAccountTransactionError(dispatch) {
  dispatch({ type: "CLEAR_ACCOUNT_TRANSACTION_ERROR" });
}

export function clearAccountBalanceData(dispatch) {
  dispatch({ type: "CLEAR_ACCOUNT_BALANCE_DATA" });
}

// Period-lock status fans out to every accounting write surface, and a single
// page mounts several consumers at once (page body + banner + modal + one cell
// per row) while StrictMode double-invokes their effects. Without this map that
// is a double-digit GET count per navigation, so the first caller wins and the
// rest await its promise.
const periodRequests = new Map();

export async function requestPeriodStatus(dispatch, payload) {
  const { businessID, force = false } = payload;

  if (!businessID) return null;
  if (!force && periodRequests.has(businessID)) {
    return periodRequests.get(businessID);
  }

  const task = (async () => {
    try {
      dispatch({ type: "REQUEST_PERIOD_STATUS", payload: { businessID } });

      // Deliberately not stored in state.request: that slot is shared and gets
      // nulled/cancelled by unrelated accounting actions. This GET is idempotent
      // and cheap, so it is never cancelled.
      const request = get({
        apiName: "accounting",
        path: `/period?business_id=${businessID}`,
      });

      const { body } = await request.response;
      const res = await body.json();

      if (res.success === false) {
        dispatch({
          type: "PERIOD_STATUS_ERROR",
          error: res.error || "Gagal memuat status periode",
        });
        return null;
      }

      dispatch({
        type: "PERIOD_STATUS_SUCCESS",
        payload: { businessID, status: res.data },
      });
      return res.data;
    } catch (error) {
      if (!isCancelError(error)) {
        dispatch({ type: "PERIOD_STATUS_ERROR", error });
      }
      return null;
    } finally {
      periodRequests.delete(businessID);
    }
  })();

  periodRequests.set(businessID, task);
  return task;
}

export async function requestClosePeriod(dispatch, payload) {
  const { businessID, through, fiscalYear, reason } = payload;

  dispatch({ type: "REQUEST_CLOSE_PERIOD" });

  try {
    // The API accepts exactly one of `through` / `fiscal_year` — sending both
    // (or an explicit undefined) is rejected.
    const body = { business_id: businessID };
    if (fiscalYear != null) {
      body.fiscal_year = fiscalYear;
    } else {
      body.through = through;
    }
    if (reason) body.reason = reason;

    const request = post({
      apiName: "accounting",
      path: "/period",
      options: { body },
    });

    const { body: resBody } = await request.response;
    const res = await resBody.json();

    if (res.success === false) {
      dispatch({
        type: "CLOSE_PERIOD_ERROR",
        error: res.error || "Gagal menutup periode",
      });
      return null;
    }

    dispatch({
      type: "CLOSE_PERIOD_SUCCESS",
      payload: { businessID, result: res.data },
    });
    return res.data;
  } catch (error) {
    if (!isCancelError(error)) {
      dispatch({ type: "CLOSE_PERIOD_ERROR", error });
    }
    return null;
  }
}

export async function requestReopenPeriod(dispatch, payload) {
  const { businessID, through, reason } = payload;

  dispatch({ type: "REQUEST_REOPEN_PERIOD" });

  try {
    // `reason` is required by the API — it is what makes self-service reopening
    // auditable. Omitting `through` reopens fully (closedThrough -> null).
    const body = { business_id: businessID, reason };
    if (through) body.through = through;

    const request = put({
      apiName: "accounting",
      path: "/period",
      options: { body },
    });

    const { body: resBody } = await request.response;
    const res = await resBody.json();

    if (res.success === false) {
      dispatch({
        type: "REOPEN_PERIOD_ERROR",
        error: res.error || "Gagal membuka kembali periode",
      });
      return null;
    }

    dispatch({
      type: "REOPEN_PERIOD_SUCCESS",
      payload: { businessID, result: res.data },
    });
    return res.data;
  } catch (error) {
    if (!isCancelError(error)) {
      dispatch({ type: "REOPEN_PERIOD_ERROR", error });
    }
    return null;
  }
}

export function clearPeriodResult(dispatch) {
  dispatch({ type: "CLEAR_PERIOD_RESULT" });
}

const createAccount = /* GraphQL */ `
  mutation CreateAccount($input: CreateAccountInput!) {
    createAccount(input: $input) {
      id
      businessID
      name
      code
      category
      type
      createdAt
      updatedAt
    }
  }
`;

const accountsByBusinessCode = /* GraphQL */ `
  query AccountsByBusinessCode(
    $businessID: ID
    $code: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelBusinessAccountFilterInput
    $limit: Int
    $nextToken: String
  ) {
    accountsByBusinessCode(
      businessID: $businessID
      code: $code
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        businessID
        code
        name
        category
        type
        createdAt
        editable
        transactions {
          nextToken
        }
        updatedAt
      }
      nextToken
    }
  }
`;

const transactionsByBusinessCreatedAt = /* GraphQL */ `
  query TransactionsByBusinessCreatedAt(
    $businessID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelBusinessAccountingTransactionFilterInput
    $limit: Int
    $nextToken: String
  ) {
    transactionsByBusinessCreatedAt(
      businessID: $businessID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        invoiceNumber
        referenceNumber
        payrollRunID
        stockAdjustmentID
        depositID
        depositNumber
        businessID
        business {
          id
          name
          type
          category
          taxCategory
          taxNumber
          address
          phone
          owner
          observers
          alwaysSync
          cashIncomeDebitAccountID
          cashIncomeCreditAccountID
          cashExpenseDebitAccountID
          cashExpenseCreditAccountID
          createdAt
          updatedAt
        }
        type
        description
        accounts {
          items {
            id
            accountID
            transactionID
            businessID
            debit
            credit
            createdAt
            account {
              id
              businessID
              code
              name
              category
              type
              createdAt
              editable
            }
          }
          nextToken
        }
        total
        paid
        parentTransactionID
        payments {
          items {
            id
            businessID
            type
            depositID
            depositNumber
            description
            total
            paid
            parentTransactionID
            paymentDueAt
            partnerID
            createdAt
            updatedAt
          }
          nextToken
        }
        items {
          items {
            id
            accountingTransactionID
            productID
            product {
              id
              name
              type
              price
              categoryID
              category {
                id
                name
                createdAt
                updatedAt
              }
              cost
              reference
              sellable
              purchasable
              taxBaseType
              taxBaseRate
              taxBasePercentage
              taxBaseFractionNumerator
              taxBaseFractionDenumerator
              currentStock
              stockLastUpdatedAt
              createdAt
              updatedAt
            }
            taxID
            taxComputation
            taxRate
            taxAccountID
            taxes {
              taxID
              kind
              withholding
              computation
              rate
              accountID
              amount
            }
            tax {
              id
              name
              computation
              kind
              withholding
              type
              amount
              accountID
              account {
                id
                code
                name
                category
                type
                createdAt
                updatedAt
              }
              scope
              createdAt
              updatedAt
            }
            quantity
            price
            description
            unit
            taxBaseType
            taxBaseRate
            taxBasePercentage
            taxBaseFractionNumerator
            taxBaseFractionDenumerator
            createdAt
            updatedAt
          }
          nextToken
        }
        deductInventory
        trackInventory
        discount {
          type
          value
          applyAfterTax
        }
        taxRounding
        roundingAmount
        paymentDueAt
        vatCreditPeriod
        partnerID
        partner {
          id
          businessID
          name
          email
          phone
          type
          taxNumber
          address
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const getBusinessAccountingTransaction = /* GraphQL */ `
  query GetBusinessAccountingTransaction($id: ID!) {
    getBusinessAccountingTransaction(id: $id) {
      id
      invoiceNumber
      referenceNumber
      payrollRunID
      stockAdjustmentID
      depositID
      depositNumber
      businessID
      business {
        id
        name
        type
        category
        taxCategory
        transactions {
          nextToken
        }
        products {
          nextToken
        }
        partners {
          nextToken
        }
        taxNumber
        address
        phone
        createdAt
        updatedAt
      }
      type
      description
      accounts {
        items {
          id
          accountID
          transactionID
          businessID
          debit
          credit
          account {
            id
            code
            name
            category
            type
          }
          createdAt
          updatedAt
        }
        nextToken
      }
      total
      paid
      parentTransactionID
      payments {
        items {
          id
          businessID
          type
          depositID
          depositNumber
          description
          total
          paid
          parentTransactionID
          paymentDueAt
          partnerID
          createdAt
          updatedAt
        }
        nextToken
      }
      items {
        items {
          id
          accountingTransactionID
          productID
          product {
            id
            name
            type
            price
            categoryID
            category {
              id
              name
              createdAt
              updatedAt
            }
            cost
            reference
            sellable
            purchasable
            taxBaseType
            taxBaseRate
            taxBasePercentage
            taxBaseFractionNumerator
            taxBaseFractionDenumerator
            currentStock
            stockLastUpdatedAt
            createdAt
            updatedAt
          }
          taxID
          taxComputation
          taxRate
          taxAccountID
          taxes {
            taxID
            kind
            withholding
            computation
            rate
            accountID
            amount
          }
          tax {
            id
            name
            computation
            kind
            withholding
            type
            amount
            accountID
            account {
              id
              code
              name
              category
              type
              createdAt
              updatedAt
            }
            scope
            createdAt
            updatedAt
          }
          quantity
          price
          description
          unit
          taxBaseType
          taxBaseRate
          taxBasePercentage
          taxBaseFractionNumerator
          taxBaseFractionDenumerator
          createdAt
          updatedAt
        }
        nextToken
      }
      deductInventory
      trackInventory
      discount {
        type
        value
        applyAfterTax
      }
      taxRounding
      roundingAmount
      paymentDueAt
      vatCreditPeriod
      partnerID
      partner {
        id
        businessID
        name
        email
        phone
        type
        taxNumber
        address
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

const transactionsByPartnerCreatedAt = /* GraphQL */ `
  query TransactionsByPartnerCreatedAt(
    $partnerID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelBusinessAccountingTransactionFilterInput
    $limit: Int
    $nextToken: String
  ) {
    transactionsByPartnerCreatedAt(
      partnerID: $partnerID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        invoiceNumber
        business {
          id
          name
          type
          category
          taxCategory
          taxNumber
          address
          phone
          createdAt
          updatedAt
        }
        type
        description
        accounts {
          items {
            id
            accountID
            transactionID
            businessID
            debit
            credit
            account {
              id
              code
              name
              category
              type
            }
            createdAt
            updatedAt
          }
          nextToken
        }
        total
        paid
        parentTransactionID
        payments {
          items {
            id
            businessID
            type
            depositID
            depositNumber
            description
            total
            paid
            parentTransactionID
            paymentDueAt
            partnerID
            createdAt
            updatedAt
          }
          nextToken
        }
        items {
          items {
            id
            accountingTransactionID
            productID
            product {
              id
              name
              type
              price
              categoryID
              category {
                id
                name
                createdAt
                updatedAt
              }
              cost
              reference
              sellable
              purchasable
              taxBaseType
              taxBaseRate
              taxBasePercentage
              taxBaseFractionNumerator
              taxBaseFractionDenumerator
              currentStock
              stockLastUpdatedAt
              createdAt
              updatedAt
            }
            taxID
            taxComputation
            taxRate
            taxAccountID
            taxes {
              taxID
              kind
              withholding
              computation
              rate
              accountID
              amount
            }
            tax {
              id
              name
              computation
              kind
              withholding
              type
              amount
              accountID
              account {
                id
                code
                name
                category
                type
                createdAt
                updatedAt
              }
              scope
              createdAt
              updatedAt
            }
            quantity
            price
            description
            unit
            taxBaseType
            taxBaseRate
            taxBasePercentage
            taxBaseFractionNumerator
            taxBaseFractionDenumerator
            createdAt
            updatedAt
          }
          nextToken
        }
        deductInventory
        trackInventory
        discount {
          type
          value
          applyAfterTax
        }
        taxRounding
        roundingAmount
        paymentDueAt
        vatCreditPeriod
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const getAccountingTransactionImport = /* GraphQL */ `
  query GetAccountingTransactionImport($id: ID!) {
    getAccountingTransactionImport(id: $id) {
      id
      useDetection
      status
      businessID
      business {
        id
        name
      }
      file {
        bucket
        region
        key
        identityId
      }
      transactions {
        id
        description
        type
        createdAt
        accounts {
          id
          code
          category
          name
          type
          debit
          credit
        }
      }
      errors
      createdAt
      expiredAt
      updatedAt
    }
  }
`;

const importsByBusinessCreatedAt = /* GraphQL */ `
  query ImportsByBusinessCreatedAt(
    $businessID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelAccountingTransactionImportFilterInput
    $limit: Int
    $nextToken: String
  ) {
    importsByBusinessCreatedAt(
      businessID: $businessID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        useDetection
        status
        businessID
        business {
          id
          name
        }
        file {
          bucket
          region
          key
          identityId
        }
        transactions {
          id
          description
          type
          createdAt
        }
        errors
        createdAt
        expiredAt
        updatedAt
      }
      nextToken
    }
  }
`;

const accountTransactionsByAccountCreatedAt = /* GraphQL */ `
  query AccountTransactionsByAccountCreatedAt(
    $accountID: ID
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelAccountingAccountTransactionFilterInput
    $limit: Int
    $nextToken: String
  ) {
    accountTransactionsByAccountCreatedAt(
      accountID: $accountID
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        accountID
        transactionID
        businessID
        debit
        credit
        account {
          id
          businessID
          code
          name
          category
          type
          createdAt
          editable
          updatedAt
        }
        transaction {
          id
          invoiceNumber
          referenceNumber
          payrollRunID
          stockAdjustmentID
          depositID
          depositNumber
          type
          description
          total
          paid
          deductInventory
          trackInventory
          discount {
            type
            value
            applyAfterTax
          }
          paymentDueAt
          payments {
            items {
              id
            }
          }
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;
